import React, { useState, useEffect } from "react";
import checkPermission from '../Utils/checkPermission';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Typography,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import ExpenseFormDialog from "./ExpenseFormDialog";
import DeleteConfirmDialog from "../Utils/DeleteConfirmDialog";
import axiosApi from "../Utils/axiosApi";

const HostlerAdditionalChargeList = ({
  open,
  onClose,
  expenses = [],
  onSave,
  hostelId,
  hostelName,
  hostelerId,
  hostelerName,
}) => {
  const [expenseList, setExpenseList] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ✅ normalize function moved ABOVE useEffect
  const normalizeExpense = (expense) => {
    if (!expense) return expense;
    return {
      ...expense,
      expenseStartDate: expense.expenseStartDate
        ? expense.expenseStartDate.substring(0, 10)
        : "",
      expenseEndDate: expense.expenseEndDate
        ? expense.expenseEndDate.substring(0, 10)
        : "",
      expenseType: expense.expenseType || "-",
      description: expense.expenseDetails || "", // ✅ map from backend field
      remarks: expense.remarks || "",
      additionalExpenseAmount: expense.additionalExpenseAmount || 0,
    };
  };

  useEffect(() => {
    setExpenseList(expenses.map((e) => normalizeExpense(e)));
  }, [expenses]);

  const handleAdd = () => {
    setEditingExpense(null);
    setFormOpen(true);
  };

  const handleEdit = async (expense) => {
    try {
      const response = await axiosApi.post("/hosteler/getExpense", {
        expenseIds: [expense.id],
      });

      if (response.status === 200 && response.data?.length > 0) {
        setEditingExpense(normalizeExpense(response.data[0]));
      } else {
        setEditingExpense(normalizeExpense(expense));
      }
    } catch (err) {
      console.error("Error fetching expense:", err);
      setEditingExpense(normalizeExpense(expense));
    }
    setFormOpen(true);
  };

  const handleFormSave = (expense) => {
    const normalized = normalizeExpense({
      ...expense,
      hostelId,
      hostelName,
      hostelerId,
      hostelerName,
    });

    let updated;
    if (editingExpense) {
      updated = expenseList.map((e) =>
        e.id === normalized.id ? { ...e, ...normalized } : e
      );
    } else {
      updated = [...expenseList, normalized];
    }

    setExpenseList(updated);
    if (onSave) onSave(updated);
  };

  const askDelete = (expense) => {
    setDeleteTarget(expense);
    setDeleteOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget?.id) {
      setDeleteOpen(false);
      return;
    }

    try {
      setDeleting(true);
      await axiosApi.post("/hostelerAdditionalExpense/deleteHostelerAdditionalExpense", {
        hostelerExpenseIds: [deleteTarget.id],
      });

      const updated = expenseList.filter((e) => e.id !== deleteTarget.id);
      setExpenseList(updated);
      if (onSave) onSave(updated);
    } catch (err) {
      console.error("Failed to delete:", err);
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
      setDeleteTarget(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") onClose();
      }}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>Manage Additional Charges</DialogTitle>

      <DialogContent dividers>
        {expenseList.length === 0 ? (
          <Typography align="center" sx={{ py: 3 }}>
            No expenses recorded yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Hosteler Name</TableCell>
                <TableCell>Hostel Name</TableCell>
                <TableCell>Expense Start Date</TableCell>
                <TableCell>Expense End Date</TableCell>
                <TableCell>Expense Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Remarks</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {expenseList.map((expense) => (
                <TableRow key={expense.id || expense.expenseStartDate}>
                  <TableCell>{expense.hostelerName || "-"}</TableCell>
                  <TableCell>{expense.hostelName || "-"}</TableCell>
                  <TableCell>{expense.expenseStartDate || "-"}</TableCell>
                  <TableCell>{expense.expenseEndDate || "-"}</TableCell>
                  <TableCell>{expense.expenseType || "-"}</TableCell>
                  <TableCell>{expense.expenseDetails || "-"}</TableCell>
                  <TableCell>{expense.remarks || "-"}</TableCell>
                  <TableCell>{expense.additionalExpenseAmount}</TableCell>

                  <TableCell align="center">
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        justifyContent: "center",
                      }}
                    >
                      <Button disabled={!checkPermission("Hostel", "Hosteler List", "VIEWADDEXP")}
                        size="small"
                        variant="outlined"
                        onClick={() => handleEdit(expense)}
                      >
                        Edit
                      </Button>
                      <Button disabled={!checkPermission("Hostel", "Hosteler List", "DELETEADDEXP")}
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => askDelete(expense)}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>

      <DialogActions>
        <Button disabled={!checkPermission("Hostel", "Hosteler List", "CREATEADDEXP")} variant="outlined" startIcon={<AddIcon />} onClick={handleAdd}>
          Add Expense
        </Button>
        <Button variant="contained" color="primary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>

      {/* Expense Form */}
      <ExpenseFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSave={handleFormSave}
        expense={editingExpense}
        hostelId={hostelId}
        hostelName={hostelName}
        hostelerId={hostelerId}
        hostelerName={hostelerName}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirmed}
        title="Delete Expense"
        message={`Are you sure you want to delete this expense from ${
          deleteTarget?.expenseStartDate || "-"
        } to ${deleteTarget?.expenseEndDate || "-"}?`}
        loading={deleting}
      />
    </Dialog>
  );
};

export default HostlerAdditionalChargeList;
